"use client";

import { useState } from "react";
import {
  Download,
  Save,
  FileText,
  Heart,
  BarChart3,
  RefreshCw,
  Users,
  TrendingUp,
  Target,
  User,
  Award,
  Briefcase,
  Brain,
  CheckCircle,
  XCircle,
  Activity,
} from "lucide-react";
import "./user-role-detailed-report.css";

const UserRoleDetailedReport = ({ comparisonData }) => {
  console.log(comparisonData, "comparisonData from <UserRoleDetailedReport />");
  const [activeSection, setActiveSection] = useState("overview");
  const [activeSubTab, setActiveSubTab] = useState("analysis");
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);
  const [filename, setFilename] = useState("");
  const comparisonResult = comparisonData.detailedReport;
  // Dummy data structure based on the specification
  const userRoleData = {
    title: "User vs Role Analysis Report",
    role: {
      name: "Software Engineer",
      department: "Engineering",
      level: "Mid-Level",
      shortDescription:
        "Builds and maintains scalable software solutions with modern engineering practices.",
      fullDescription:
        "Responsible for designing, developing, and maintaining software applications using modern programming languages and frameworks. Collaborates with cross-functional teams to deliver high-quality solutions that meet business requirements and technical standards.",
      requiredHardSkills: [
        "Java",
        "Git",
        "Agile Methodologies",
        "Python",
        "Docker",
        "Microservices",
      ],
      keySoftSkills: [
        "Communication",
        "Problem-Solving",
        "Adaptability",
        "Team Collaboration",
      ],
      educationRequired: "Bachelor's Degree",
      majors: ["Computer Science", "Software Engineering"],
      lifeVectors: {
        Spirit: { trait: "Inquisitive", score: 8 },
        Rewards: { trait: "Impact-oriented", score: 7 },
        Profession: { trait: "Structured", score: 9 },
        Purpose: { trait: "Mission-driven", score: 10 },
      },
    },
    user: {
      name: "Daniel Lee",
      currentRole: "Junior Developer",
      department: "Technology",
      education: "Bachelor's in Computer Science",
      experience: "3 years",
      leadershipScore: 72,
      hardSkills: ["Java", "Git", "Agile Methodologies"],
      softSkills: ["Problem Solving", "Team Collaboration", "Communication"],
      lifeVectors: {
        Spirit: { trait: "Extremely versatile", score: 8 },
        Rewards: { trait: "Achievement-driven", score: 7 },
        Profession: { trait: "Methodical", score: 9 },
        Purpose: { trait: "Security-focused", score: 6 },
      },
    },
    metrics: {
      missingHardSkills: 3,
      missingSoftSkills: 1,
      alignedLifeVectors: 3,
      notAlignedLifeVectors: 1,
      mobilityFeasibilityScore: 65,
      overallAlignmentRating: "Moderate",
    },
    skillAnalysis: {
      hardSkillsAlignment: "Moderate",
      hardSkillsCoverage: 50, // 3 of 6 skills
      softSkillsAlignment: "High",
      softSkillsCoverage: 75, // 3 of 4 skills
      hardAligned: ["Java", "Git", "Agile Methodologies"],
      hardMissing: ["Python", "Docker", "Microservices"],
      softAligned: ["Problem Solving", "Team Collaboration", "Communication"],
      softMissing: ["Adaptability"],
    },
    personalityFit: {
      alignmentRating: "Moderate",
      alignmentPercentage: 75, // 3 of 4 vectors
      alignedVectors: ["Spirit", "Rewards", "Profession"],
      notAlignedVectors: ["Purpose"],
    },
    mobilityFeasibility: {
      readinessScore: 65,
      alignedSkills: [
        "Communication",
        "Problem Solving",
        "Team Collaboration",
        "Java",
        "Git",
        "Agile Methodologies",
      ],
      gapSkills: [
        "Python",
        "Docker",
        "Microservices",
        "Adaptability",
        "Purpose",
      ],
    },
    analysis: {
      skillMatch:
        "Daniel shows moderate skill alignment: he covers 3 of 6 hard skills (50%) and 3 of 4 soft skills (75%). His strengths in teamwork and communication support immediate collaboration, but gaps in Python, Docker, Microservices, and adaptability need targeted development.",
      personalityFit:
        "Daniel aligns on 3 of 4 life vectors: Spirit, Rewards, and Profession. His versatile, achievement-driven, and methodical profile maps closely to the role's inquisitive, impact-oriented, and structured expectations. The single gap is Purpose — security-focused vs mission-driven — which could affect long-term motivation unless reframed.",
      mobilityFeasibility:
        "Daniel's overall readiness is Moderate (65%). He is strong in soft skills and partially aligned in technical and aptitude areas. Gaps in backend/cloud technologies, adaptability, and Purpose orientation suggest a medium training effort. With structured technical upskilling and adaptability coaching, Daniel can feasibly transition into this role.",
    },
  };

  const sections = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "userRoleDetails", label: "User and Role Details", icon: Users },
    { id: "skillMatchIndex", label: "Skill Match Index", icon: Target },
    { id: "personalityFit", label: "Personality Fit", icon: Brain },
    {
      id: "mobilityFeasibility",
      label: "Mobility Feasibility",
      icon: Activity,
    },
    { id: "summary", label: "Summary", icon: FileText },
  ];

  const vis = comparisonResult?.personalityFit?.visualization ?? {};
  const lifeVectors = Array.isArray(vis.lifeVectors) ? vis.lifeVectors : [];
  const alignedSet = new Set(
    Array.isArray(vis.aligned)
      ? vis.aligned
      : lifeVectors
          .filter((v) => String(v.match).toLowerCase() === "aligned")
          .map((v) => v.name)
  );

  const renderSubTabs = (sectionId) => {
    if (
      ["skillMatchIndex", "personalityFit", "mobilityFeasibility"].includes(
        sectionId
      )
    ) {
      const labels = {
        skillMatchIndex: ["Analysis", "Skills Comparison"],
        personalityFit: ["Analysis", "Life Vector Fit"],
        mobilityFeasibility: ["Analysis", "Readiness Details"],
      };

      return (
        <div className="ur-detailed-sub-tabs">
          <button
            className={`ur-detailed-sub-tab ${
              activeSubTab === "analysis" ? "active" : ""
            }`}
            onClick={() => setActiveSubTab("analysis")}
          >
            {labels[sectionId][0]}
          </button>
          <button
            className={`ur-detailed-sub-tab ${
              activeSubTab === "visualization" ? "active" : ""
            }`}
            onClick={() => setActiveSubTab("visualization")}
          >
            {labels[sectionId][1]}
          </button>
        </div>
      );
    }
    return null;
  };

  const renderSkillPills = (skills, type) => {
    return (
      <div className="ur-detailed-skill-pills">
        {skills.map((skill, index) => (
          <span
            key={index}
            className={`ur-detailed-skill-pill ur-detailed-${type}`}
          >
            {skill}
          </span>
        ))}
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="ur-detailed-section-content">
            <div className="ur-detailed-overview-grid">
              {/* Role */}
              <div className="ur-detailed-overview-card ur-detailed-primary">
                <div className="ur-detailed-overview-icon">
                  <Briefcase size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>Role</h3>
                  <div className="ur-detailed-score-display">
                    {comparisonResult.overview.roleName}
                  </div>
                  <p>Target role</p>
                </div>
              </div>

              {/* User */}
              <div className="ur-detailed-overview-card ur-detailed-secondary">
                <div className="ur-detailed-overview-icon">
                  <User size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>User</h3>
                  <div className="ur-detailed-score-display">
                    {comparisonResult.overview.userName}
                  </div>
                  <p>Candidate</p>
                </div>
              </div>

              {/* Missing Hard Skills */}
              <div className="ur-detailed-overview-card ur-detailed-accent">
                <div className="ur-detailed-overview-icon">
                  <XCircle size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>Missing Hard Skills</h3>
                  <div className="ur-detailed-score-display">
                    {comparisonResult.overview.missingHardSkills}
                  </div>
                  <p>Skills to develop</p>
                </div>
              </div>

              {/* Missing Soft Skills */}
              <div className="ur-detailed-overview-card ur-detailed-warning">
                <div className="ur-detailed-overview-icon">
                  <Heart size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>Missing Soft Skills</h3>
                  <div className="ur-detailed-score-display">
                    {/* {userRoleData.metrics.missingSoftSkills} */}
                    {comparisonResult.overview.missingSoftSkills}
                  </div>
                  <p>Behavioral gaps</p>
                </div>
              </div>

              {/* Aligned Life Vectors */}
              <div className="ur-detailed-overview-card ur-detailed-success">
                <div className="ur-detailed-overview-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>Aligned Life Vectors</h3>
                  <div className="ur-detailed-score-display">
                    {/* {userRoleData.metrics.alignedLifeVectors} */}
                    {comparisonResult.overview.alignedLifeVectors}
                  </div>
                  <p>Personality matches</p>
                </div>
              </div>

              {/* Not Aligned Life Vectors */}
              <div className="ur-detailed-overview-card ur-detailed-danger">
                <div className="ur-detailed-overview-icon">
                  <XCircle size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>Not Aligned Life Vectors</h3>
                  <div className="ur-detailed-score-display">
                    {/* {userRoleData.metrics.notAlignedLifeVectors} */}
                    {comparisonResult.overview.notAlignedLifeVectors}
                  </div>
                  <p>Personality gaps</p>
                </div>
              </div>

              {/* Mobility Feasibility Score */}
              <div className="ur-detailed-overview-card ur-detailed-info">
                <div className="ur-detailed-overview-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>Mobility Feasibility Score</h3>
                  <div className="ur-detailed-score-display">
                    {/* {userRoleData.metrics.mobilityFeasibilityScore}% */}
                    {comparisonResult.overview.mobilityFeasibilityScore}%
                  </div>
                  <p>Transition readiness</p>
                </div>
              </div>

              {/* Overall Alignment Rating */}
              <div className="ur-detailed-overview-card ur-detailed-dark">
                <div className="ur-detailed-overview-icon">
                  <Award size={24} />
                </div>
                <div className="ur-detailed-overview-content">
                  <h3>Overall Alignment Rating</h3>
                  <div className="ur-detailed-score-display">
                    {/* {userRoleData.metrics.overallAlignmentRating} */}
                    {comparisonResult.overview.overallRating}
                  </div>
                  <p>Fit assessment</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "userRoleDetails":
        return (
          <div className="ur-detailed-section-content">
            <h3 style={{ marginBottom: "10px" }}>User and Role Details</h3>
            <p style={{ marginBottom: "20px" }}>
              Comprehensive profiles including personal details, role
              requirements, and qualifications.
            </p>
            <div className="ur-detailed-details-grid">
              {/* User Card */}
              <div className="ur-detailed-details-card">
                <div className="ur-detailed-details-header">
                  <h4>{comparisonResult.userDetails.userName}</h4>
                </div>
                <div className="ur-detailed-details-info">
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">
                      Current Role:
                    </span>

                    <span className="ur-detailed-info-value">
                      {comparisonResult.userDetails.designation}
                    </span>
                    {}
                  </div>
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">Department:</span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.userDetails.department}
                    </span>
                  </div>
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">Education:</span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.userDetails.education}
                    </span>
                  </div>
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">Experience:</span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.userDetails.experience}
                    </span>
                  </div>
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">
                      Leadership Score:
                    </span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.userDetails.leadershipScore}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Role Card */}

              <div className="ur-detailed-details-card">
                <div className="ur-detailed-details-header">
                  <h4>{comparisonResult.roleDetails.roleName}</h4>
                </div>
                <div className="ur-detailed-details-info">
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">Department:</span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.roleDetails.department}
                    </span>
                  </div>
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">Level:</span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.roleDetails.level}
                    </span>
                  </div>
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">
                      Education Required:
                    </span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.roleDetails.education.join(", ")}
                    </span>
                  </div>
                  <div className="ur-detailed-info-item">
                    <span className="ur-detailed-info-label">Majors:</span>
                    <span className="ur-detailed-info-value">
                      {comparisonResult.roleDetails.majors.join(", ")}
                    </span>
                  </div>
                </div>
                <div className="ur-detailed-role-description">
                  <h5>Role Description</h5>
                  <p>{comparisonResult.roleDetails.roleDescription}</p>
                </div>
                {/*<div className="ur-detailed-role-skills">
                  <h5>Required Hard Skills</h5>
                  <div className="ur-detailed-skill-list">{userRoleData.role.requiredHardSkills.join(", ")}</div>
                </div>
                <div className="ur-detailed-role-skills">
                  <h5>Key Soft Skills</h5>
                  <div className="ur-detailed-skill-list">{userRoleData.role.keySoftSkills.join(", ")}</div>
                </div>*/}
              </div>
            </div>
          </div>
        );

      case "skillMatchIndex":
        return (
          <div className="ur-detailed-section-content">
            {renderSubTabs("skillMatchIndex")}
            {activeSubTab === "analysis" ? (
              <div className="ur-detailed-analysis-container">
                <h3>Skill Match Index Analysis</h3>
                <div className="ur-detailed-analysis-content">
                  <p>{comparisonResult.skillMatchIndex.analysis}</p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Skills Comparison</h3>
                <p style={{ marginBottom: "20px" }}>
                  Visual comparison of hard and soft skills coverage with
                  aligned and missing skills breakdown.
                </p>

                {/* Coverage Bars */}
                <div className="ur-detailed-coverage-section">
                  <h4>Skills Coverage</h4>
                  <div className="ur-detailed-coverage-bars">
                    <div className="ur-detailed-coverage-item">
                      <div className="ur-detailed-coverage-header">
                        <span className="ur-detailed-coverage-label">
                          Hard Skills Coverage
                        </span>
                        <span className="ur-detailed-coverage-percentage">
                          {
                            comparisonResult.skillMatchIndex.visualization
                              .hardSkillsCoverage
                          }
                          %
                        </span>
                      </div>
                      <div className="ur-detailed-coverage-bar">
                        <div
                          className="ur-detailed-coverage-fill ur-detailed-hard-skills"
                          style={{
                            width: `${comparisonResult.skillMatchIndex.visualization.hardSkillsCoverage}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="ur-detailed-coverage-item">
                      <div className="ur-detailed-coverage-header">
                        <span className="ur-detailed-coverage-label">
                          Soft Skills Coverage
                        </span>
                        <span className="ur-detailed-coverage-percentage">
                          {
                            comparisonResult.skillMatchIndex.visualization
                              .softSkillsCoverage
                          }
                          %
                        </span>
                      </div>
                      <div className="ur-detailed-coverage-bar">
                        <div
                          className="ur-detailed-coverage-fill ur-detailed-soft-skills"
                          style={{
                            width: `${comparisonResult.skillMatchIndex.visualization.softSkillsCoverage}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills Pills */}
                <div className="ur-detailed-skills-pills-section">
                  <div className="ur-detailed-skills-category">
                    <h4>Hard Skills</h4>
                    <div className="ur-detailed-skills-group">
                      <h5>Aligned</h5>
                      {comparisonResult.skillMatchIndex.visualization
                        .hardSkillsAlignment.aligned.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No aligned hard skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.skillMatchIndex.visualization
                          .hardSkillsAlignment.aligned,
                        "aligned"
                      )}
                    </div>
                    <div className="ur-detailed-skills-group">
                      <h5>Missing</h5>
                      {comparisonResult.skillMatchIndex.visualization
                        .hardSkillsAlignment.missing.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No missing hard skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.skillMatchIndex.visualization
                          .hardSkillsAlignment.missing,
                        "missing"
                      )}
                    </div>
                  </div>

                  <div className="ur-detailed-skills-category">
                    <h4>Soft Skills</h4>
                    <div className="ur-detailed-skills-group">
                      <h5>Aligned</h5>
                      {comparisonResult.skillMatchIndex.visualization
                        .softSkillsAlignment.aligned.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No aligned soft skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.skillMatchIndex.visualization
                          .softSkillsAlignment.aligned,
                        "aligned"
                      )}
                    </div>
                    <div className="ur-detailed-skills-group">
                      <h5>Missing</h5>
                      {comparisonResult.skillMatchIndex.visualization
                        .softSkillsAlignment.missing.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No missing soft skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.skillMatchIndex.visualization
                          .softSkillsAlignment.missing,
                        "missing"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "personalityFit":
        return (
          <div className="ur-detailed-section-content">
            {renderSubTabs("personalityFit")}
            {activeSubTab === "analysis" ? (
              <div className="ur-detailed-analysis-container">
                <h3>Personality Fit Analysis</h3>
                <div className="ur-detailed-analysis-content">
                  <p>{comparisonResult.personalityFit.analysis}</p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Life Vector Fit</h3>
                <p style={{ marginBottom: "20px" }}>
                  Personality alignment across four life vectors showing user
                  traits vs role requirements.
                </p>

                {/* Alignment Bar */}
                <div className="ur-detailed-alignment-section">
                  <h4>Life Vectors Alignment</h4>
                  <div className="ur-detailed-alignment-bar-container">
                    <div className="ur-detailed-alignment-header">
                      <span className="ur-detailed-alignment-label">
                        Overall Alignment
                      </span>
                      <span className="ur-detailed-alignment-percentage">
                        {
                          comparisonResult.personalityFit.visualization
                            .overallPersonalityFitScore
                        }
                        %
                      </span>
                    </div>
                    <div className="ur-detailed-alignment-bar">
                      <div
                        className="ur-detailed-alignment-fill"
                        style={{
                          width: `${comparisonResult.personalityFit.visualization.overallPersonalityFitScore}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Life Vectors Comparison */}
                <div className="ur-detailed-vectors-section">
                  <h4>Life Vectors Comparison</h4>
                  <div className="ur-detailed-vectors-grid">
                    {lifeVectors.length === 0 ? (
                      <div className="ur-detailed-vector-card">
                        <div className="ur-detailed-vector-header">
                          <h5>Personality Vectors</h5>
                          <span className="ur-detailed-vector-status ur-detailed-not-aligned">
                            No data
                          </span>
                        </div>
                        <div className="ur-detailed-vector-comparison">
                          <div className="ur-detailed-vector-item">
                            <span className="ur-detailed-vector-trait">
                              No vector details available.
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      lifeVectors.map((v) => {
                        const isAligned =
                          alignedSet.has(v.name) ||
                          String(v.match).toLowerCase() === "aligned";

                        return (
                          <div key={v.name} className="ur-detailed-vector-card">
                            <div className="ur-detailed-vector-header">
                              <h5>{v.name}</h5>
                              <span
                                className={`ur-detailed-vector-status ${
                                  isAligned
                                    ? "ur-detailed-aligned"
                                    : "ur-detailed-not-aligned"
                                }`}
                              >
                                {isAligned ? "Aligned" : "Not Aligned"}
                              </span>
                            </div>
                            <div className="ur-detailed-vector-comparison">
                              <div className="ur-detailed-vector-item">
                                <span className="ur-detailed-vector-label">
                                  User:
                                </span>
                                <span className="ur-detailed-vector-trait">
                                  {v.user_trait}
                                </span>
                              </div>
                              <div className="ur-detailed-vector-item">
                                <span className="ur-detailed-vector-label">
                                  Role:
                                </span>
                                <span className="ur-detailed-vector-trait">
                                  {v.role_trait}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Vector Pills */}
                <div className="ur-detailed-vectors-pills-section">
                  <div className="ur-detailed-vectors-group">
                    <h5>Aligned</h5>
                    {comparisonResult.personalityFit.visualization.aligned
                      .length === 0 && (
                      <span className="ur-detailed-no-skills">
                        No aligned life vectors
                      </span>
                    )}
                    {renderSkillPills(
                      comparisonResult.personalityFit.visualization.aligned,
                      "aligned"
                    )}
                  </div>
                  <div className="ur-detailed-vectors-group">
                    <h5>Not Aligned</h5>
                    {comparisonResult.personalityFit.visualization.notAligned
                      .length === 0 && (
                      <span className="ur-detailed-no-skills">
                        No non-aligned life vectors
                      </span>
                    )}
                    {renderSkillPills(
                      comparisonResult.personalityFit.visualization.notAligned,
                      "missing"
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "mobilityFeasibility":
        return (
          <div className="ur-detailed-section-content">
            {renderSubTabs("mobilityFeasibility")}
            {activeSubTab === "analysis" ? (
              <div className="ur-detailed-analysis-container">
                <h3>Mobility Feasibility Analysis</h3>
                <div className="ur-detailed-analysis-content">
                  <p>{comparisonResult.mobilityFeasibility.analysis}</p>
                </div>
              </div>
            ) : (
              <div>
                <h3 style={{ marginBottom: "10px" }}>Readiness Details</h3>
                <p style={{ marginBottom: "20px" }}>
                  Overall mobility readiness score with breakdown of aligned
                  skills and development gaps by category.
                </p>

                {/* Mobility Score Bar */}
                <div className="ur-detailed-mobility-section">
                  <h4>Mobility Readiness</h4>
                  <div className="ur-detailed-mobility-bar-container">
                    <div className="ur-detailed-mobility-header">
                      <span className="ur-detailed-mobility-label">
                        Overall Readiness
                      </span>
                      <span className="ur-detailed-mobility-percentage">
                        {
                          comparisonResult.mobilityFeasibility.visualization
                            .overallReadiness
                        }
                        %
                      </span>
                    </div>
                    <div className="ur-detailed-mobility-bar">
                      <div
                        className="ur-detailed-mobility-fill"
                        style={{
                          width: `${comparisonResult.mobilityFeasibility.visualization.overallReadiness}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Readiness Pills */}
                <div className="ur-detailed-readiness-pills-section">
                  <div className="ur-detailed-readiness-category">
                    <h4>Hard Skills</h4>
                    <div className="ur-detailed-readiness-group">
                      <h5>Aligned Skills</h5>
                      {comparisonResult.mobilityFeasibility.visualization
                        .hardSkillsAlignment.aligned.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No aligned hard skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.mobilityFeasibility.visualization
                          .hardSkillsAlignment.aligned,
                        "aligned"
                      )}
                    </div>
                    <div className="ur-detailed-readiness-group">
                      <h5>Development Gaps</h5>
                      {comparisonResult.mobilityFeasibility.visualization
                        .hardSkillsAlignment.missing.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No missing hard skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.mobilityFeasibility.visualization
                          .hardSkillsAlignment.missing,
                        "missing"
                      )}
                    </div>
                  </div>

                  <div className="ur-detailed-readiness-category">
                    <h4>Soft Skills</h4>
                    <div className="ur-detailed-readiness-group">
                      <h5>Aligned Skills</h5>
                      {comparisonResult.mobilityFeasibility.visualization
                        .softSkillsAlignment.aligned.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No aligned soft skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.mobilityFeasibility.visualization
                          .softSkillsAlignment.aligned,
                        "aligned"
                      )}
                    </div>
                    <div className="ur-detailed-readiness-group">
                      <h5>Development Gaps</h5>
                      {comparisonResult.mobilityFeasibility.visualization
                        .softSkillsAlignment.missing.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No missing soft skills
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.mobilityFeasibility.visualization
                          .softSkillsAlignment.missing,
                        "missing"
                      )}
                    </div>
                  </div>

                  <div className="ur-detailed-readiness-category">
                    <h4>Aptitude</h4>
                    <div className="ur-detailed-readiness-group">
                      <h5>Aligned Traits</h5>
                      {comparisonResult.mobilityFeasibility.visualization
                        .aptitudeAlignment.aligned.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No aligned aptitude traits
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.mobilityFeasibility.visualization
                          .aptitudeAlignment.aligned,
                        "aligned"
                      )}
                    </div>
                    <div className="ur-detailed-readiness-group">
                      <h5>Development Gaps</h5>
                      {comparisonResult.mobilityFeasibility.visualization
                        .aptitudeAlignment.notAligned.length === 0 && (
                        <span className="ur-detailed-no-skills">
                          No missing aptitude traits
                        </span>
                      )}
                      {renderSkillPills(
                        comparisonResult.mobilityFeasibility.visualization
                          .aptitudeAlignment.notAligned,
                        "missing"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case "summary":
        return (
          <div className="ur-detailed-section-content">
            <div className="ur-detailed-summary-content">
              <div className="ur-detailed-summary-section">
                <h4>Skill Match Index</h4>
                <p>{comparisonResult.summary.skillMatch}</p>
              </div>
              <div className="ur-detailed-summary-section">
                <h4>Personality Fit</h4>
                <p>{comparisonResult.summary.personalityFit}</p>
              </div>
              <div className="ur-detailed-summary-section">
                <h4>Mobility Feasibility</h4>
                <p>{comparisonResult.summary.mobilityFeasibility}</p>
              </div>
            </div>
          </div>
        );

      default:
        return <div>Section not found</div>;
    }
  };

  const handleDownloadPDF = () => {
    const element = document.createElement("a");
    const file = new Blob(
      [
        `User vs Role Analysis Report\n\nGenerated on: ${new Date().toLocaleDateString()}`,
      ],
      {
        type: "text/plain",
      }
    );
    element.href = URL.createObjectURL(file);
    element.download = `user-role-analysis-report-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generateDefaultFilename = () => {
    const date = new Date();
    const dateStr = date.toISOString().split("T")[0];
    return `user-role-analysis-report-${dateStr}`;
  };

  const handleSaveReport = () => {
    const defaultName = generateDefaultFilename();
    setFilename(defaultName);
    setShowFilenameModal(true);
  };

  const confirmSaveReport = () => {
    if (!filename.trim()) {
      alert("Please enter a filename");
      return;
    }
    setShowFilenameModal(false);
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowSaveModal(true);
    }, 1500);
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setActiveSubTab("analysis");
  };

  return (
    <div className="ur-detailed-comparison-page">
      <main className="detailed-comparison-main">
        <div className="container">
          {/* Header */}
          <div className="detailed-comparison-header">
            <div className="detailed-header-content">
              <div className="detailed-header-text">
                <h1>{userRoleData.title}</h1>
                <p>
                  Comprehensive user-role compatibility and mobility analysis
                </p>
              </div>
              <div
                className="detailed-header-actions"
                style={{ display: "none" }}
              >
                <button
                  className="detailed-action-btn detailed-secondary"
                  onClick={handleDownloadPDF}
                >
                  <Download size={16} />
                  Download PDF
                </button>
                <button
                  className="detailed-action-btn detailed-primary"
                  onClick={handleSaveReport}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Report
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="ur-detailed-comparison-layout">
            {/* Navigation Sidebar */}
            <div className="ur-detailed-navigation">
              <div className="ur-detailed-nav-header">
                <h3>Report Sections</h3>
              </div>
              <nav className="ur-detailed-nav-menu">
                {sections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      className={`ur-detailed-nav-item ${
                        activeSection === section.id ? "active" : ""
                      }`}
                      onClick={() => handleSectionChange(section.id)}
                    >
                      <IconComponent size={18} />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Main Content */}
            <div className="ur-detailed-content">
              <div className="ur-detailed-content-header">
                <h2>{sections.find((s) => s.id === activeSection)?.label}</h2>
              </div>
              {renderSection()}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserRoleDetailedReport;
